import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { EChartsOption } from "echarts";
import * as echarts from "echarts";
import { NgxEchartsDirective, provideEchartsCore } from "ngx-echarts";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Item } from "../../../pojo/Item";
import { NameService } from "../../../services/name.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { UserStore } from "../../../stores/user.store";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { EngagementActivity, EngagementBadge, EngagementBadgeEarned, EngagementService } from "./engagement.service";
import { ACTIVITY_TYPE_LABELS, ENGAGEMENT_LABELS } from "./engagement.labels";

@TypeManagerDecorator("s25-ng-se-user-engagement-detail")
@Component({
    selector: "s25-ng-se-user-engagement-detail",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideEchartsCore({ echarts })],
    imports: [CommonModule, NgxEchartsDirective, S25LoadingInlineStaticComponent],
    template: `
        <div class="se-user-engagement">
            <h2 class="se-user-engagement__title">{{ engagementLabels.featureName }} — {{ contactLabel() }}</h2>

            @if (historyRes.resource.error()) {
                <p class="se-user-engagement__error">{{ historyRes.resource.error()!.message }}</p>
            }
            @if (badgesRes.resource.error()) {
                <p class="se-user-engagement__error">{{ badgesRes.resource.error()!.message }}</p>
            }

            <section class="se-user-engagement__section">
                <h3>Activity over time</h3>
                @if (historyRes.resource.isLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading history…'" />
                } @else {
                    <div class="se-user-engagement__chart" echarts [options]="chartOptions()"></div>
                }
            </section>

            <section class="se-user-engagement__section">
                <h3>Activity log</h3>
                @if (historyRes.resource.isLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading activities…'" />
                } @else {
                    <table class="se-user-engagement__table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Date</th>
                                <th>Organization</th>
                                <th>Event</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of sortedActivities(); track row.activityId) {
                                <tr>
                                    <td>{{ activityTypeLabel(row.activityTypeKey) }}</td>
                                    <td>{{ formatDateTime(row.activityDt) }}</td>
                                    <td>{{ orgName(row.organizationId) }}</td>
                                    <td>{{ eventName(row.eventId) }}</td>
                                </tr>
                            } @empty {
                                <tr>
                                    <td colspan="4" class="se-user-engagement__empty">No activities recorded.</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }
            </section>

            <section class="se-user-engagement__section">
                <h3>{{ engagementLabels.badgesTitle }}</h3>
                @if (badgesRes.resource.isLoading()) {
                    <s25-ng-loading-inline-static [text]="'Loading badges…'" />
                } @else {
                    <ul class="se-user-engagement__badges">
                        @for (b of sortedBadges(); track b.earned.earnedId) {
                            <li class="se-user-engagement__badge">
                                <span class="se-user-engagement__badge-key">{{ b.name }}</span>
                                @if (b.description?.trim()) {
                                    <p class="se-user-engagement__badge-desc">{{ b.description }}</p>
                                }
                                <span class="se-user-engagement__badge-dt"
                                    >Earned {{ formatDateTime(b.earned.earnedDt) }}</span
                                >
                            </li>
                        } @empty {
                            <li class="se-user-engagement__empty">No badges earned yet.</li>
                        }
                    </ul>
                }
            </section>
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .se-user-engagement__title {
                margin: 0 0 1.25rem;
            }
            .se-user-engagement__section {
                margin-bottom: 2rem;
            }
            .se-user-engagement__section h3 {
                margin: 0 0 0.75rem;
                font-size: 1.1rem;
            }
            .se-user-engagement__chart {
                height: 320px;
                width: 100%;
            }
            .se-user-engagement__table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.95rem;
            }
            .se-user-engagement__table th,
            .se-user-engagement__table td {
                border: 1px solid var(--color--border);
                padding: 0.45rem 0.6rem;
                text-align: left;
            }
            .se-user-engagement__table th {
                background: var(--color--surface-2-bg);
            }
            .se-user-engagement__error {
                color: var(--color--danger);
                margin: 0 0 1rem;
            }
            .se-user-engagement__empty {
                color: var(--color--text-muted);
                font-style: italic;
            }
            .se-user-engagement__badges {
                list-style: none;
                margin: 0;
                padding: 0;
                display: grid;
                gap: 0.5rem;
            }
            .se-user-engagement__badge {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.35rem;
                padding: 0.5rem 0.75rem;
                border: 1px solid var(--color--border);
                border-radius: 6px;
                background: var(--color--surface-2-bg);
            }
            .se-user-engagement__badge-key {
                font-weight: 600;
            }
            .se-user-engagement__badge-desc {
                margin: 0;
                font-size: 0.88rem;
                color: var(--color--text);
                line-height: 1.45;
            }
            .se-user-engagement__badge-dt {
                color: var(--color--text);
                font-size: 0.9rem;
            }
        `,
    ],
})
export class SeUserEngagementDetailComponent {
    readonly contId = input.required<number>();

    readonly engagementLabels = ENGAGEMENT_LABELS;
    private readonly userStore = inject(UserStore);

    readonly historyRes = s25Resource({
        params: () => this.contId(),
        loader: ({ params }) => EngagementService.getUserHistory(params),
    });

    readonly badgesRes = s25Resource({
        params: () => this.contId(),
        loader: ({ params }) => EngagementService.getUserBadges(params),
    });

    readonly badgeDefsRes = s25Resource(() => EngagementService.getBadges());

    readonly namesRes = s25Resource({
        params: () => ({
            contId: this.contId(),
            activities: this.historyRes.value() ?? [],
        }),
        loader: async ({ params: { contId, activities } }) => {
            const items: { itemId: number; itemTypeId: number }[] = [{ itemId: contId, itemTypeId: Item.Ids.Contact }];
            for (const activity of activities) {
                if (activity.organizationId != null) {
                    items.push({ itemId: activity.organizationId, itemTypeId: Item.Ids.Organization });
                }
                if (activity.eventId != null) {
                    items.push({ itemId: activity.eventId, itemTypeId: Item.Ids.Event });
                }
            }
            return NameService.getNames(items);
        },
    });

    readonly contactLabel = computed(() => {
        const names = this.namesRes.value();
        if (!names) return "";
        const key = NameService.itemKeyFromIds(this.contId(), Item.Ids.Contact);
        return names[key] ?? `Contact ${this.contId()}`;
    });

    readonly chartOptions = computed((): EChartsOption => {
        const rows = this.historyRes.value() ?? [];
        const byDate = new Map<string, number>();
        for (const row of rows) {
            const parsed = S25Util.date.parse(row.activityDt);
            if (!parsed || isNaN(parsed.getTime())) continue;
            const key = parsed.toISOString().slice(0, 10);
            byDate.set(key, (byDate.get(key) ?? 0) + 1);
        }
        const dates = [...byDate.keys()].sort();
        const counts = dates.map((d) => byDate.get(d) ?? 0);
        return {
            tooltip: { trigger: "axis" },
            grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
            xAxis: { type: "category", boundaryGap: false, data: dates },
            yAxis: { type: "value", name: "Activities" },
            series: [
                {
                    name: "Activities",
                    type: "line",
                    smooth: true,
                    data: counts,
                    areaStyle: {},
                },
            ],
        };
    });

    readonly sortedActivities = computed((): EngagementActivity[] => {
        const rows = this.historyRes.value() ?? [];
        return [...rows].sort((a, b) => (b.activityDt ?? "").localeCompare(a.activityDt ?? ""));
    });

    readonly sortedBadges = computed((): { earned: EngagementBadgeEarned; name: string; description?: string }[] => {
        const rows = this.badgesRes.value() ?? [];
        const defs = this.badgeDefsRes.value() ?? [];
        const defById = new Map<number, EngagementBadge>(
            defs.filter((d) => d.badgeId != null).map((d) => [d.badgeId!, d]),
        );
        return [...rows]
            .sort((a, b) => (b.earnedDt ?? "").localeCompare(a.earnedDt ?? ""))
            .map((e) => {
                const def = defById.get(e.badgeId);
                return {
                    earned: e,
                    name: def?.name ?? String(e.badgeId),
                    description: def?.description,
                };
            });
    });

    activityTypeLabel(key: string): string {
        return ACTIVITY_TYPE_LABELS[key] ?? key;
    }

    orgName(orgId: number | undefined): string {
        if (orgId == null) return "—";
        const names = this.namesRes.value();
        if (!names) return "";
        return names[NameService.itemKeyFromIds(orgId, Item.Ids.Organization)] ?? String(orgId);
    }

    eventName(eventId: number | undefined): string {
        if (eventId == null) return "—";
        const names = this.namesRes.value();
        if (!names) return "";
        return names[NameService.itemKeyFromIds(eventId, Item.Ids.Event)] ?? String(eventId);
    }

    formatDateTime(iso: string | undefined): string {
        if (!iso) return "";
        const fmt = this.userStore.dateTimeFormat();
        if (!fmt) return iso;
        return S25Datefilter.transform(S25Util.date.parse(iso), fmt) || iso;
    }
}
